/**
 * Blocked property and the reports owed to OFAC (31 CFR 501.603, 501.604; S-33).
 *
 * The designated compliance officer (or a super_admin) sees what is blocked, files each report through the
 * OFAC Reporting System and records it here (date, OFAC reference, the copy that was filed), records the
 * license or delisting that releases property, and confirms a release another person recorded. Nothing of
 * this reaches a client or an Account Executive.
 */
import { useCallback, useEffect, useState } from 'react';
import { limitToCents } from '../../lib/amount';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import { FormAlert } from '../../components/forms';
import UsEntitySelect from '../../components/staff/UsEntitySelect';
import ofacService, {
  type BlockedProperty,
  type ConfirmedMatch,
  type EvidenceKind,
  type ReportTask,
  type UsEntity,
} from '../../services/ofacBlocking.service';
import { cleanMfaInput, isMfaCode, stepUpErrorMessage } from '../../lib/mfaApproval';
import {
  blockSubject,
  canFile,
  canPlace,
  canRecordEvidence,
  canRecordRejected,
  evidenceLabel,
  hasDelistingToVerify,
  heldLine,
  isLate,
  kindLabel,
  taskState,
} from '../../lib/ofacBlocking';
import { formatDate, formatDateTime } from '../../lib/datetime';
import { colors } from '../../styles/theme';

/** Called when a write worked, with the sentence the person is shown. */
type Done = (message: string) => void;

const INPUT_STYLE = { border: `1px solid ${colors.border}` };
const INPUT_CLASS = 'w-full px-3 py-2 rounded-lg text-sm';

function Field({
  id,
  label,
  children,
}: Readonly<{ id: string; label: string; children: JSX.Element }>): JSX.Element {
  return (
    <div className="mt-3">
      <label className="block text-xs mb-1" htmlFor={id} style={{ color: colors.muted }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function CodeField({
  id,
  value,
  onChange,
}: Readonly<{ id: string; value: string; onChange: (v: string) => void }>): JSX.Element {
  return (
    <Field id={id} label="Authentication code">
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={value}
        onChange={(e) => onChange(cleanMfaInput(e.target.value))}
        className="px-3 py-2 rounded-lg text-sm font-mono tracking-widest w-36"
        style={INPUT_STYLE}
      />
    </Field>
  );
}

function Send({
  disabled,
  label,
  error,
  onClick,
}: Readonly<{ disabled: boolean; label: string; error: string | null; onClick: () => void }>): JSX.Element {
  return (
    <div className="mt-3">
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className="text-sm px-3 py-2 rounded-lg font-semibold"
        style={{ background: colors.gold, color: colors.navy }}
      >
        {label}
      </button>
      {error && (
        <p className="text-sm mt-3" role="alert" style={{ color: colors.danger }}>
          {error}
        </p>
      )}
    </div>
  );
}

function useAction(onDone: (message: string) => void, success: string): {
  busy: boolean;
  error: string | null;
  run: (fn: () => Promise<void>, fallback: string) => Promise<void>;
} {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>, fallback: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onDone(success);
    } catch (e: unknown) {
      setError(stepUpErrorMessage(e, fallback));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

function PlacementForm({ task, onDone }: Readonly<{ task: ReportTask; onDone: Done }>): JSX.Element {
  const [noCash, setNoCash] = useState(false);
  const [date, setDate] = useState('');
  const [account, setAccount] = useState('');
  const [bank, setBank] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const { busy, error, run } = useAction(onDone, 'Recorded where the funds were placed.');
  return (
    <div className="mt-4">
      <p className="text-xs" style={{ color: colors.muted }}>
        Before this report is filed, record that the blocked funds were placed in the US entity&apos;s blocked
        interest-bearing account. The transfer itself is made at the bank.
      </p>
      <label className="flex items-center gap-2 text-sm mt-3">
        <input type="checkbox" checked={noCash} onChange={(e) => setNoCash(e.target.checked)} />
        {' '}
        This block holds no cash
      </label>
      {noCash ? (
        <Field id={`pl-reason-${task.id}`} label="Why there is no cash">
          <textarea id={`pl-reason-${task.id}`} value={reason} rows={2} maxLength={1000} onChange={(e) => setReason(e.target.value)} className={INPUT_CLASS} style={INPUT_STYLE} />
        </Field>
      ) : (
        <>
          <Field id={`pl-date-${task.id}`} label="Date the funds were placed">
            <input id={`pl-date-${task.id}`} type="date" value={date} onChange={(e) => setDate(e.target.value)} className={INPUT_CLASS} style={INPUT_STYLE} />
          </Field>
          <Field id={`pl-account-${task.id}`} label="Blocked account (reference)">
            <input id={`pl-account-${task.id}`} type="text" value={account} maxLength={120} onChange={(e) => setAccount(e.target.value)} className={INPUT_CLASS} style={INPUT_STYLE} />
          </Field>
          <Field id={`pl-bank-${task.id}`} label="The bank's reference">
            <input id={`pl-bank-${task.id}`} type="text" value={bank} maxLength={120} onChange={(e) => setBank(e.target.value)} className={INPUT_CLASS} style={INPUT_STYLE} />
          </Field>
          <Field id={`pl-amount-${task.id}`} label="Amount placed">
            <input id={`pl-amount-${task.id}`} type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(limitToCents(e.target.value))} className={INPUT_CLASS} style={INPUT_STYLE} />
          </Field>
          <Field id={`pl-currency-${task.id}`} label="Currency (three letters)">
            <input id={`pl-currency-${task.id}`} type="text" value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} className={INPUT_CLASS} style={INPUT_STYLE} translate="no" />
          </Field>
          <Field id={`pl-file-${task.id}`} label="Bank proof (PDF, PNG or JPEG)">
            <input id={`pl-file-${task.id}`} type="file" accept="application/pdf,image/png,image/jpeg" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm" />
          </Field>
        </>
      )}
      <CodeField id={`pl-code-${task.id}`} value={code} onChange={setCode} />
      <Send
        disabled={busy || !canPlace({ noCash, date, account, bank, amount, currency, file, reason, code })}
        label="Record where the funds were placed"
        error={error}
        onClick={() => {
          void run(
            () =>
              ofacService.placement(task.id, {
                placed_on: noCash ? null : date,
                account_reference: noCash ? null : account.trim(),
                bank_reference: noCash ? null : bank.trim(),
                amount: noCash ? null : amount.trim(),
                currency_code: noCash ? null : currency.trim(),
                attachment: noCash ? null : file,
                no_cash_reason: noCash ? reason.trim() : null,
                mfa_code: code.trim(),
              }),
            'Could not record where the funds were placed.',
          );
        }}
      />
    </div>
  );
}

function FilingForm({ task, onDone }: Readonly<{ task: ReportTask; onDone: Done }>): JSX.Element {
  const [date, setDate] = useState('');
  const [reference, setReference] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [code, setCode] = useState('');
  const { busy, error, run } = useAction(onDone, 'The report is recorded as filed.');
  return (
    <div className="mt-4">
      <Field id={`date-${task.id}`} label="Date it was filed">
        <input id={`date-${task.id}`} type="date" value={date} onChange={(e) => setDate(e.target.value)} className={INPUT_CLASS} style={INPUT_STYLE} />
      </Field>
      <Field id={`ref-${task.id}`} label="Identification number OFAC assigned">
        <input id={`ref-${task.id}`} type="text" value={reference} maxLength={120} onChange={(e) => setReference(e.target.value)} className={INPUT_CLASS} style={INPUT_STYLE} />
      </Field>
      <Field id={`file-${task.id}`} label="Copy of the report that was filed (PDF, PNG or JPEG)">
        <input id={`file-${task.id}`} type="file" accept="application/pdf,image/png,image/jpeg" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm" />
      </Field>
      <CodeField id={`code-${task.id}`} value={code} onChange={setCode} />
      <Send
        disabled={busy || !canFile({ reference, file, code, date })}
        label="Record the filing"
        error={error}
        onClick={() => {
          if (file === null) return;
          void run(
            () => ofacService.file(task.id, { filed_on: date, ofac_reference: reference.trim(), attachment: file, mfa_code: code.trim() }),
            'Could not record the filing.',
          );
        }}
      />
    </div>
  );
}

function NotRequiredForm({ task, onDone }: Readonly<{ task: ReportTask; onDone: Done }>): JSX.Element {
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const { busy, error, run } = useAction(onDone, 'Recorded that no report is required.');
  const ready = reason.trim().length >= 10 && isMfaCode(code.trim());
  return (
    <div className="mt-6 pt-4" style={{ borderTop: `1px solid ${colors.border}` }}>
      <p className="text-xs" style={{ color: colors.muted }}>
        An unblocking under a license, or after a delisting, may need no report. Another officer than the one
        who recorded or confirmed the release decides.
      </p>
      <Field id={`nr-${task.id}`} label="Why no report is required">
        <textarea id={`nr-${task.id}`} value={reason} rows={2} maxLength={1000} onChange={(e) => setReason(e.target.value)} className={INPUT_CLASS} style={INPUT_STYLE} />
      </Field>
      <CodeField id={`nrcode-${task.id}`} value={code} onChange={setCode} />
      <Send
        disabled={busy || !ready}
        label="Record that no report is required"
        error={error}
        onClick={() => { void run(() => ofacService.notRequired(task.id, reason.trim(), code.trim()), 'Could not record the decision.'); }}
      />
    </div>
  );
}

function TaskCard({ task, onDone }: Readonly<{ task: ReportTask; onDone: Done }>): JSX.Element {
  const state = taskState(task);
  const open = task.outcome === null;
  const openCopy = async (): Promise<void> => {
    window.open(await ofacService.fileLink(task.id), '_blank', 'noopener');
  };
  return (
    <li className="rounded-2xl p-5" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold" style={{ color: colors.ink }}>{kindLabel(task.kind)}</p>
          <p className="text-xs" style={{ color: colors.muted }}>
            Due {formatDate(task.due_on)} · opened {formatDateTime(task.opened_at)}
            {task.as_of ? ` · property held on ${formatDate(task.as_of)}` : ''}
          </p>
        </div>
        {state === 'overdue' && <StatusBadge tone="danger">Report overdue</StatusBadge>}
        {state === 'due_today' && <StatusBadge tone="warning">Due today</StatusBadge>}
        {state === 'open' && <StatusBadge tone="warning">{task.days_left} days left</StatusBadge>}
        {state === 'filed' && <StatusBadge tone="neutral">Filed</StatusBadge>}
        {state === 'not_required' && <StatusBadge tone="neutral">No report required</StatusBadge>}
      </div>
      {task.outcome === 'filed' && (
        <p className="text-sm mt-3" style={{ color: colors.inkSoft }}>
          <span translate="no">{task.ofac_reference}</span> · {task.filed_on ? formatDate(task.filed_on) : ''}
          {task.has_file && (
            <button type="button" className="ml-3 underline" onClick={() => { void openCopy(); }}>
              Open the copy
            </button>
          )}
        </p>
      )}
      {task.outcome === 'not_required' && (
        <p className="text-sm mt-3 whitespace-pre-wrap" style={{ color: colors.inkSoft }}>{task.reason}</p>
      )}
      {task.placement_outcome === 'placed' && (
        <p className="text-sm mt-3" style={{ color: colors.inkSoft }}>
          Funds placed in <span translate="no">{task.account_reference}</span> on{' '}
          {task.placed_on ? formatDate(task.placed_on) : ''}
        </p>
      )}
      {task.placement_outcome === 'no_cash' && (
        <p className="text-sm mt-3 whitespace-pre-wrap" style={{ color: colors.inkSoft }}>{task.placement_reason}</p>
      )}
      {open && task.needs_placement && <PlacementForm task={task} onDone={onDone} />}
      {open && !task.needs_placement && <FilingForm task={task} onDone={onDone} />}
      {open && task.kind === 'unblocked' && <NotRequiredForm task={task} onDone={onDone} />}
    </li>
  );
}

function ReleaseForm({ block, onDone }: Readonly<{ block: BlockedProperty; onDone: Done }>): JSX.Element {
  const [kind, setKind] = useState<EvidenceKind>('ofac_license');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [code, setCode] = useState('');
  const { busy, error, run } = useAction(
    onDone,
    'The evidence is recorded. A different person confirms the release.',
  );
  return (
    <div className="mt-4">
      <p className="text-xs" style={{ color: colors.muted }}>
        Property is released only with an OFAC license or a delisting, and a different person confirms it.
      </p>
      <Field id={`kind-${block.id}`} label="Evidence">
        <select id={`kind-${block.id}`} value={kind} onChange={(e) => setKind(e.target.value as EvidenceKind)} className={INPUT_CLASS} style={INPUT_STYLE}>
          <option value="ofac_license">An OFAC license</option>
          <option value="sdn_delisting">A delisting</option>
        </select>
      </Field>
      <Field id={`eref-${block.id}`} label="License number or OFAC notice">
        <input id={`eref-${block.id}`} type="text" value={reference} maxLength={120} onChange={(e) => setReference(e.target.value)} className={INPUT_CLASS} style={INPUT_STYLE} />
      </Field>
      <Field id={`ereason-${block.id}`} label="Why the property is released">
        <textarea id={`ereason-${block.id}`} value={reason} rows={2} maxLength={1000} onChange={(e) => setReason(e.target.value)} className={INPUT_CLASS} style={INPUT_STYLE} />
      </Field>
      <Field id={`efile-${block.id}`} label="Copy of the license or notice (PDF, PNG or JPEG)">
        <input id={`efile-${block.id}`} type="file" accept="application/pdf,image/png,image/jpeg" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm" />
      </Field>
      <CodeField id={`ecode-${block.id}`} value={code} onChange={setCode} />
      <Send
        disabled={busy || !canRecordEvidence({ reference, reason, code })}
        label="Record the evidence"
        error={error}
        onClick={() => {
          void run(
            async () => {
              await ofacService.recordEvidence(block.id, { evidence_kind: kind, evidence_reference: reference.trim(), reason: reason.trim(), attachment: file, mfa_code: code.trim() });
            },
            'Could not record the evidence.',
          );
        }}
      />
    </div>
  );
}

function ConfirmRelease({
  block,
  requestId,
  onDone,
}: Readonly<{ block: BlockedProperty; requestId: string; onDone: Done }>): JSX.Element {
  const [code, setCode] = useState('');
  const { busy, error, run } = useAction(onDone, 'The property is released. The report of the unblocking is now owed.');
  const [opening, setOpening] = useState<string | null>(null);
  const openCopy = async (): Promise<void> => {
    setOpening(null);
    try {
      window.open(await ofacService.evidenceLink(requestId), '_blank', 'noopener');
    } catch {
      setOpening('Could not open the copy.');
    }
  };
  return (
    <div className="mt-4">
      <p className="text-xs" style={{ color: colors.muted }}>
        Evidence of a release is recorded. A different person than the one who recorded it confirms it. Read it
        first.
      </p>
      {block.pending_evidence_kind && (
        <dl className="mt-3 text-sm" style={{ color: colors.inkSoft }}>
          <dt className="text-xs" style={{ color: colors.muted }}>Evidence</dt>
          <dd>{evidenceLabel(block.pending_evidence_kind)}</dd>
          <dt className="text-xs mt-2" style={{ color: colors.muted }}>License number or OFAC notice</dt>
          <dd translate="no">{block.pending_evidence_reference}</dd>
          <dt className="text-xs mt-2" style={{ color: colors.muted }}>Why the property is released</dt>
          <dd className="whitespace-pre-wrap">{block.pending_evidence_reason}</dd>
          {block.pending_evidence_recorded_at && (
            <>
              <dt className="text-xs mt-2" style={{ color: colors.muted }}>Recorded</dt>
              <dd>{formatDateTime(block.pending_evidence_recorded_at)}</dd>
            </>
          )}
        </dl>
      )}
      {block.pending_evidence_has_file ? (
        <p className="text-sm mt-2">
          <button type="button" className="underline" onClick={() => { void openCopy(); }}>
            Open the copy of the license or notice
          </button>
          {opening && <span className="ml-3" role="alert" style={{ color: colors.danger }}>{opening}</span>}
        </p>
      ) : (
        <p className="text-sm mt-2" style={{ color: colors.muted }}>No copy of the license or notice was attached.</p>
      )}
      <CodeField id={`confirm-${requestId}`} value={code} onChange={setCode} />
      <Send
        disabled={busy || !isMfaCode(code.trim())}
        label="Confirm the release"
        error={error}
        onClick={() => { void run(() => ofacService.confirmRelease(requestId, code.trim()), 'Could not confirm the release.'); }}
      />
    </div>
  );
}

const DELISTED_NOTICE =
  'The latest sanctions list no longer names this person. Verify the delisting with OFAC and record it below to release the property; a different person confirms it.';

function DelistingForm({ match, onDone }: Readonly<{ match: ConfirmedMatch; onDone: Done }>): JSX.Element {
  const id = match.screening_result_id;
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [code, setCode] = useState('');
  const { busy, error, run } = useAction(onDone, 'The delisting is recorded. A different person confirms it.');
  return (
    <div className="mt-4">
      <p className="text-xs" style={{ color: colors.muted }}>
        A confirmed match ends only with a delisting, and a different person confirms it. Nothing is released by it:
        the account stays suspended until its reactivation, and a new match waits for a review.
      </p>
      <Field id={`dref-${id}`} label="OFAC notice">
        <input id={`dref-${id}`} type="text" value={reference} maxLength={120} onChange={(e) => setReference(e.target.value)} className={INPUT_CLASS} style={INPUT_STYLE} />
      </Field>
      <Field id={`dreason-${id}`} label="Why the person is delisted">
        <textarea id={`dreason-${id}`} value={reason} rows={2} maxLength={1000} onChange={(e) => setReason(e.target.value)} className={INPUT_CLASS} style={INPUT_STYLE} />
      </Field>
      <Field id={`dfile-${id}`} label="Copy of the notice (PDF, PNG or JPEG)">
        <input id={`dfile-${id}`} type="file" accept="application/pdf,image/png,image/jpeg" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="text-sm" />
      </Field>
      <CodeField id={`dcode-${id}`} value={code} onChange={setCode} />
      <Send
        disabled={busy || !canRecordEvidence({ reference, reason, code })}
        label="Record the delisting"
        error={error}
        onClick={() => {
          void run(
            () => ofacService.recordDelisting(id, { evidence_reference: reference.trim(), reason: reason.trim(), attachment: file, mfa_code: code.trim() }),
            'Could not record the delisting.',
          );
        }}
      />
    </div>
  );
}

function ConfirmDelisting({
  match,
  requestId,
  onDone,
}: Readonly<{ match: ConfirmedMatch; requestId: string; onDone: Done }>): JSX.Element {
  const [code, setCode] = useState('');
  const { busy, error, run } = useAction(onDone, 'The delisting is confirmed. The person is no longer a confirmed match.');
  const [opening, setOpening] = useState<string | null>(null);
  const openCopy = async (): Promise<void> => {
    setOpening(null);
    try {
      window.open(await ofacService.delistingLink(requestId), '_blank', 'noopener');
    } catch {
      setOpening('Could not open the copy.');
    }
  };
  return (
    <div className="mt-4">
      <p className="text-xs" style={{ color: colors.muted }}>
        A delisting is recorded. A different person than the one who recorded it confirms it. Read it first.
      </p>
      <dl className="mt-3 text-sm" style={{ color: colors.inkSoft }}>
        <dt className="text-xs" style={{ color: colors.muted }}>OFAC notice</dt>
        <dd translate="no">{match.pending_evidence_reference}</dd>
        <dt className="text-xs mt-2" style={{ color: colors.muted }}>Why the person is delisted</dt>
        <dd className="whitespace-pre-wrap">{match.pending_evidence_reason}</dd>
        {match.pending_evidence_recorded_at && (
          <>
            <dt className="text-xs mt-2" style={{ color: colors.muted }}>Recorded</dt>
            <dd>{formatDateTime(match.pending_evidence_recorded_at)}</dd>
          </>
        )}
      </dl>
      {match.pending_evidence_has_file ? (
        <p className="text-sm mt-2">
          <button type="button" className="underline" onClick={() => { void openCopy(); }}>
            Open the copy of the notice
          </button>
          {opening && <span className="ml-3" role="alert" style={{ color: colors.danger }}>{opening}</span>}
        </p>
      ) : (
        <p className="text-sm mt-2" style={{ color: colors.muted }}>No copy of the notice was attached.</p>
      )}
      <CodeField id={`dconfirm-${requestId}`} value={code} onChange={setCode} />
      <Send
        disabled={busy || !isMfaCode(code.trim())}
        label="Confirm the delisting"
        error={error}
        onClick={() => { void run(() => ofacService.confirmDelisting(requestId, code.trim()), 'Could not confirm the delisting.'); }}
      />
    </div>
  );
}

function ConfirmedMatchCard({ match, onDone }: Readonly<{ match: ConfirmedMatch; onDone: Done }>): JSX.Element {
  return (
    <li className="rounded-2xl p-5" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold" style={{ color: colors.ink }}>
            <span translate="no">{match.client_name ?? 'A client'}</span>
          </p>
          <p className="text-xs" style={{ color: colors.muted }}>
            Confirmed a true match {formatDateTime(match.confirmed_at)} · nothing is blocked
          </p>
        </div>
        <StatusBadge tone="danger">Confirmed match</StatusBadge>
      </div>
      {hasDelistingToVerify(match) && (
        <p className="text-sm mt-3" style={{ color: colors.inkSoft }}>
          The latest sanctions list no longer names this person. Verify the delisting with OFAC and record it below;
          a different person confirms it.
        </p>
      )}
      {match.pending_request_id === null && <DelistingForm match={match} onDone={onDone} />}
      {match.pending_request_id !== null && (
        <ConfirmDelisting match={match} requestId={match.pending_request_id} onDone={onDone} />
      )}
    </li>
  );
}

function BlockCard({ block, onDone }: Readonly<{ block: BlockedProperty; onDone: Done }>): JSX.Element {
  const subject = blockSubject(block);
  return (
    <li className="rounded-2xl p-5" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold" style={{ color: colors.ink }}>
            {block.subject_kind === 'position' ? 'A holding in a US series' : 'A transfer into a US account'}
          </p>
          <p className="text-xs" style={{ color: colors.muted }}>
            <span translate="no">{subject.who}</span> · held in <span translate="no">{subject.where}</span> · blocked{' '}
            {formatDateTime(block.blocked_at)}
          </p>
          <p className="text-sm mt-2" style={{ color: colors.inkSoft }}>{heldLine(block.held)}</p>
        </div>
        <StatusBadge tone={block.in_force ? 'danger' : 'neutral'}>{block.in_force ? 'Blocked' : 'Released'}</StatusBadge>
      </div>
      {hasDelistingToVerify(block) && (
        <p className="text-sm mt-3" style={{ color: colors.inkSoft }}>{DELISTED_NOTICE}</p>
      )}
      {block.in_force && block.pending_request_id === null && <ReleaseForm block={block} onDone={onDone} />}
      {block.in_force && block.pending_request_id !== null && (
        <ConfirmRelease block={block} requestId={block.pending_request_id} onDone={onDone} />
      )}
    </li>
  );
}

/** The US entities to choose from: null while loading, `error` when the list could not be had. */
function useUsEntities(): {
  entities: UsEntity[] | null;
  error: string | null;
  reload: () => void;
} {
  const [entities, setEntities] = useState<UsEntity[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setEntities(null);
    setError(null);
    ofacService
      .usEntities()
      .then((list) => { if (!cancelled) setEntities(list); })
      .catch(() => { if (!cancelled) setError('Could not load the US legal entities.'); });
    return () => { cancelled = true; };
  }, [attempt]);
  return { entities, error, reload: () => setAttempt((n) => n + 1) };
}

function RejectedForm({ onDone }: Readonly<{ onDone: Done }>): JSX.Element {
  const usEntities = useUsEntities();
  const [entity, setEntity] = useState('');
  const [authority, setAuthority] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [code, setCode] = useState('');
  const { busy, error, run } = useAction(onDone, 'The rejected transaction is recorded; its report is now owed.');
  return (
    <div className="rounded-2xl p-5" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <p className="text-xs" style={{ color: colors.muted }}>
        A US entity rejected a transaction that is not blocked because processing it would break a sanctions
        regulation. The report is due 10 business days after the day it was rejected.
      </p>
      <Field id="rj-entity" label="Legal entity that rejected it">
        <UsEntitySelect
          id="rj-entity"
          entities={usEntities.entities}
          error={usEntities.error}
          value={entity}
          onChange={setEntity}
          onRetry={usEntities.reload}
        />
      </Field>
      <Field id="rj-authority" label="Sanctions program or provision">
        <input id="rj-authority" type="text" value={authority} maxLength={300} onChange={(e) => setAuthority(e.target.value)} className={INPUT_CLASS} style={INPUT_STYLE} />
      </Field>
      <Field id="rj-description" label="The transaction that was rejected">
        <textarea id="rj-description" value={description} rows={2} maxLength={1000} onChange={(e) => setDescription(e.target.value)} className={INPUT_CLASS} style={INPUT_STYLE} />
      </Field>
      <Field id="rj-date" label="Date it was rejected">
        <input id="rj-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={INPUT_CLASS} style={INPUT_STYLE} />
      </Field>
      <CodeField id="rj-code" value={code} onChange={setCode} />
      <Send
        disabled={busy || !canRecordRejected({ entity, authority, description, date, code })}
        label="Record the rejected transaction"
        error={error}
        onClick={() => {
          void run(
            () => ofacService.recordRejected({ legal_entity_id: entity.trim(), bank_transaction_id: null, legal_authority: authority.trim(), description: description.trim(), rejected_on: date, mfa_code: code.trim() }),
            'Could not record the rejected transaction.',
          );
        }}
      />
    </div>
  );
}

export default function OfacBlocking(): JSX.Element {
  const [tasks, setTasks] = useState<ReportTask[]>([]);
  const [blocks, setBlocks] = useState<BlockedProperty[]>([]);
  const [matches, setMatches] = useState<ConfirmedMatch[]>([]);
  const [all, setAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, b, m] = await Promise.all([
        ofacService.tasks(!all),
        ofacService.blocks(!all),
        ofacService.confirmedMatches(),
      ]);
      setTasks(t);
      setBlocks(b);
      setMatches(m);
    } catch {
      setError('Only the compliance officer and super admins can open this page.');
    } finally {
      setLoading(false);
    }
  }, [all]);

  useEffect(() => { void reload(); }, [reload]);
  const late = tasks.filter(isLate).length;
  const done: Done = (message) => {
    setNotice(message);
    void reload();
  };

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Blocked property"
        subtitle="Property blocked under OFAC's regulations, and the reports owed to OFAC."
        actions={
          <label className="flex items-center gap-2 text-sm" style={{ color: colors.inkSoft }}>
            <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
            {' '}
            Show what is closed too
          </label>
        }
      />
      {error && <p className="mb-4 text-sm" role="alert" style={{ color: colors.danger }}>{error}</p>}
      {notice && <div className="mb-4"><FormAlert variant="success">{notice}</FormAlert></div>}
      {loading && <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>}
      {!loading && !error && (
        <>
          <h2 className="text-base font-semibold mb-3" style={{ color: colors.ink }}>
            Reports to OFAC
            {late > 0 && <span className="ml-3"><StatusBadge tone="danger">{late} overdue</StatusBadge></span>}
          </h2>
          {tasks.length === 0 && <p className="text-sm mb-6" style={{ color: colors.muted }}>No report is waiting.</p>}
          <ul className="space-y-3 mb-8">
            {tasks.map((t) => <TaskCard key={t.id} task={t} onDone={done} />)}
          </ul>
          <h2 className="text-base font-semibold mb-3" style={{ color: colors.ink }}>Blocked property</h2>
          {blocks.length === 0 && <p className="text-sm mb-6" style={{ color: colors.muted }}>Nothing is blocked.</p>}
          <ul className="space-y-3 mb-8">
            {blocks.map((b) => <BlockCard key={b.id} block={b} onDone={done} />)}
          </ul>
          <h2 className="text-base font-semibold mb-3" style={{ color: colors.ink }}>Confirmed matches with nothing blocked</h2>
          {matches.length === 0 && <p className="text-sm mb-6" style={{ color: colors.muted }}>No confirmed match is in force.</p>}
          <ul className="space-y-3 mb-8">
            {matches.map((m) => <ConfirmedMatchCard key={m.screening_result_id} match={m} onDone={done} />)}
          </ul>
          <h2 className="text-base font-semibold mb-3" style={{ color: colors.ink }}>A rejected transaction</h2>
          <RejectedForm onDone={done} />
        </>
      )}
    </div>
  );
}
